import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  CreditCard, 
  Globe, 
  HeartPulse, 
  FileSpreadsheet, 
  ShieldCheck, 
  AlertTriangle, 
  PhoneCall, 
  ExternalLink, 
  ArrowRight,
  Sparkles,
  Layers,
  CheckCircle2,
  Lock
} from 'lucide-react';
import { AUSTRALIAN_DOCUMENTS, DocumentGuide } from '../data/documents';

export const IdentityDocumentsPage: React.FC = () => {
  const [selectedDocId, setSelectedDocId] = useState<string>('driver-licence');

  const selectedDoc: DocumentGuide = AUSTRALIAN_DOCUMENTS.find(d => d.id === selectedDocId) || AUSTRALIAN_DOCUMENTS[0];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-semibold border border-emerald-800">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Australian Document Protection Hub</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          Australian Identity Document Safeguards
        </h1>
        <p className="text-slate-400 text-sm max-w-xl mx-auto">
          Official replacement procedures, Document Verification Service (DVS) safeguards, and compromise recovery for all Australian identity credentials.
        </p>
      </div>

      {/* Watermark Banner Shortcut */}
      <div className="p-5 rounded-2xl bg-gradient-to-r from-teal-950 via-slate-900 to-slate-900 border border-teal-800/60 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-900 text-teal-200">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Providing an ID copy for rental or employment?</h3>
            <p className="text-xs text-slate-300">Always watermark copies to prevent unauthorized reuse or storage leaks.</p>
          </div>
        </div>
        <Link
          to="/id-watermark"
          className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-500 text-white text-xs font-bold transition-colors flex-shrink-0"
        >
          Open Watermark Generator
        </Link>
      </div>

      {/* Document Category Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-4">
        {AUSTRALIAN_DOCUMENTS.map((doc) => (
          <button
            key={doc.id}
            onClick={() => setSelectedDocId(doc.id)}
            className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${
              selectedDocId === doc.id
                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-950/40'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-800'
            }`}
          >
            {doc.name}
          </button>
        ))}
      </div>

      {/* Active Document In-Depth Guide */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl space-y-8">
        {/* Document Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-800">
                {selectedDoc.badge}
              </span>
              <span className="text-xs text-slate-400 font-mono">{selectedDoc.officialAgency}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-2">
              {selectedDoc.name}
            </h2>
          </div>

          <div className="text-xs text-slate-400 flex flex-col sm:items-end gap-1">
            <span className="font-semibold text-slate-300">Official Helpline:</span>
            <span className="text-emerald-400 font-mono font-bold text-sm">{selectedDoc.agencyPhone}</span>
          </div>
        </div>

        {/* 1. Sensitive Elements */}
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-400" />
            <span>What information is sensitive on this document?</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedDoc.sensitiveElements.map((elem, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-white">{elem.title}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-rose-950 text-rose-300 border border-rose-800">
                    {elem.riskLevel}
                  </span>
                </div>
                <p className="text-slate-400 leading-relaxed">{elem.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 2. What to do if compromised */}
        <div className="p-6 rounded-2xl bg-amber-950/20 border border-amber-800/50 space-y-3">
          <h3 className="text-sm font-bold text-amber-300 uppercase tracking-wider flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span>What to do if this document is lost, stolen, or exposed</span>
          </h3>
          <ul className="space-y-2 text-xs text-slate-200">
            {selectedDoc.whatToDoIfCompromised.map((step, idx) => (
              <li key={idx} className="flex items-start gap-2.5">
                <span className="text-amber-400 font-bold">•</span>
                <span className="leading-relaxed">{step}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 3. Official Replacement Process */}
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span>Official Australian Replacement Process</span>
          </h3>
          <div className="space-y-3">
            {selectedDoc.replacementProcess.map((step) => (
              <div key={step.step} className="p-4 rounded-xl bg-slate-950 border border-slate-800 flex items-start gap-3.5 text-xs">
                <span className="w-6 h-6 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center flex-shrink-0 text-xs">
                  {step.step}
                </span>
                <div className="space-y-1">
                  <div className="font-bold text-white text-sm">{step.title}</div>
                  <p className="text-slate-300 leading-relaxed">{step.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* State specific variations if applicable */}
        {selectedDoc.stateSpecificRules && (
          <div className="space-y-3 pt-2">
            <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider">
              State & Territory Specific Road Transport Authorities:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {selectedDoc.stateSpecificRules.map((st, i) => (
                <div key={i} className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-1 text-xs">
                  <div className="flex items-center justify-between font-bold text-white">
                    <span>{st.state} — {st.authority}</span>
                    <a href={st.contactUrl} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline flex items-center gap-0.5 text-[11px]">
                      Portal <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <p className="text-slate-400 text-[11px] leading-snug">{st.reissueNotes}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. Future Prevention */}
        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-2 text-xs">
          <h4 className="font-bold text-emerald-400 uppercase tracking-wider text-[11px]">
            Best Practices to Reduce Future Risk
          </h4>
          <ul className="space-y-1.5 text-slate-300">
            {selectedDoc.futurePreventionTips.map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
